import React, { useEffect, useRef, useState } from "react";
import { ESTADOS_MATRICULA } from "../constants/estadosMatricula";
import { listarVariantesCurso } from "../../../../services/cursosService";
import { usePermissions } from "../../../../utils/permissions";

const renderOptions = (options, placeholder, customLoading = null, loadingOptions = false) => {
  const isLoading = customLoading !== null ? customLoading : loadingOptions;
  if (isLoading) return <option value="">{`Cargando ${placeholder}...`}</option>;
  if (!options.length) return <option value="">No hay {placeholder} disponibles</option>;
  return [
    <option key="placeholder" value="">
      {`Selecciona ${placeholder}`}
    </option>,
    ...options.map((opt) => (
      <option key={opt.value} value={opt.value}>
        {opt.label}
      </option>
    )),
  ];
};

const ensureCicloExists = (options, cicloId, matriculaData = null) => {
  if (!cicloId) return options;
  const exists = options.some((opt) => String(opt.value) === String(cicloId));
  if (exists) return options;
  const label = matriculaData?.cicloLabel || `Ciclo #${cicloId}`;
  return [...options, { value: String(cicloId), label }];
};

const ensureOptionExists = (options, currentId, matriculaData = null) => {
  if (!currentId) return options;
  const exists = options.some((opt) => String(opt.value) === String(currentId));
  if (exists) return options;

  let label = `ID ${currentId} (no listado)`;
  if (matriculaData) {
    const nombre = matriculaData.promocion || "";
    const curso = matriculaData.curso || "";
    const centro = matriculaData.centro || "";
    const parts = [nombre, curso, centro].filter(Boolean);
    label = parts.length > 0 ? parts.join(" | ") : label;
  }

  return [
    ...options,
    {
      value: String(currentId),
      label,
      cicloId: matriculaData?.ciclo_id ?? matriculaData?.cicloId ?? null,
      cursoId: matriculaData?.curso_id ?? matriculaData?.cursoId ?? null,
    },
  ];
};

export default function MatriculaFormFields({
  form,
  setForm,
  isEdit = false,
  estudianteState,
  setDocumento,
  buscarEstudiante,
  resultados = [],
  seleccionarEstudiante,
  ciclosOptions = [],
  promocionesOptions = [],
  cursosOptions = [],
  loadingOptions = false,
  reloadOptions,
  matriculaData = null,
  incluirFinalizadas = false,
  onToggleIncluirFinalizadas,
  loadingFinalizadas = false,
}) {
  const { hasPermission } = usePermissions();
  const puedeEditarFecha = hasPermission("migracion.fecha_retroactiva");
  const puedeVerFinalizadas = !isEdit && hasPermission("matriculas.excepcion_migracion");

  // ── Inputs bidireccionales % ↔ $ a pagar ──────────────────────────────────
  const [precioInput, setPrecioInput] = useState("");
  const editandoPrecioRef = useRef(false);

  // Calcula el total con IVA redondeando el subtotal primero (igual que el backend)
  const calcTotal = (base, promoDesc, indDesc) => {
    if (!base) return 0;
    const subtotal = +Math.max(base - base * (promoDesc / 100) - base * (indDesc / 100), 0).toFixed(2);
    const iva = +(subtotal * 0.15).toFixed(2);
    return subtotal + iva;
  };

  // ────────────────────────────────────────────────────────────────────────────
  const termino = (estudianteState.documento || "").trim();
  const esNumerico = /^\d+$/.test(termino);
  const minChars = esNumerico ? 5 : 3;
  const puedeBuscar = !isEdit && termino.length >= minChars && !estudianteState.buscando;
  const showSearch = !isEdit;
  const promocionesConActual = ensureOptionExists(promocionesOptions, form.promocion_id, matriculaData);
  const promoSeleccionada = promocionesConActual.find((opt) => String(opt.value) === String(form.promocion_id));
  const cicloActivoId = String(form.ciclo_id || promoSeleccionada?.cicloId || "").trim();
  const ciclosConActual = ensureCicloExists(ciclosOptions, cicloActivoId || (matriculaData?.cicloId ?? null), matriculaData);
  const promocionesFiltradas = cicloActivoId
    ? promocionesConActual.filter((opt) => String(opt.cicloId || "") === cicloActivoId)
    : [];
  const promoEstado = (promoSeleccionada?.estado || "").toLowerCase();
  const promoYaInicio = promoEstado === "en_curso" || promoEstado === "cursando";
  const codigoMatricula = matriculaData?.codigo_matricula ?? matriculaData?.codigoMatricula ?? "";
  const codigoFallback = isEdit ? "No asignado" : "Se asigna al guardar";
  const estadoReservada = (form.estado_matricula || "").toLowerCase() === "reservada";
  const mostrarCurso = estadoReservada && !form.promocion_id;

  // ── Variantes/subtipos comerciales del curso (ej. Reentrenamiento N1/N2) ────
  const cursoOperativoId = mostrarCurso ? form.curso_id : promoSeleccionada?.cursoId;
  const [variantes, setVariantes] = useState([]);
  useEffect(() => {
    if (!cursoOperativoId) {
      setVariantes([]);
      return;
    }
    let cancelado = false;
    listarVariantesCurso(cursoOperativoId)
      .then((data) => { if (!cancelado) setVariantes(data || []); })
      .catch(() => { if (!cancelado) setVariantes([]); });
    return () => { cancelado = true; };
  }, [cursoOperativoId]);

  useEffect(() => {
    if (!variantes.length) return;
    const sigueValido = variantes.some((v) => String(v.id) === String(form.subtipo_curso_id));
    if (!sigueValido) {
      setForm((p) => ({ ...p, subtipo_curso_id: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantes]);

  const subtipoSeleccionado = variantes.find((v) => String(v.id) === String(form.subtipo_curso_id));

  const precioBase = subtipoSeleccionado
    ? Number(subtipoSeleccionado.precio_base)
    : isEdit
      ? (matriculaData?.precioLista || 0)
      : (promoSeleccionada?.precioBase || 0);
  const descPromoPct = isEdit
    ? (matriculaData?.descPromocionPorcentaje || 0)
    : (promoSeleccionada?.descuentoPorcentaje || 0);
  const totalCalculado = calcTotal(precioBase, descPromoPct, Number(form.descuento_individual || 0));

  // Sincroniza el input de precio cuando cambia el % (pero no cuando el usuario escribe en el campo $)
  useEffect(() => {
    if (editandoPrecioRef.current) return;
    setPrecioInput(precioBase && totalCalculado > 0 ? totalCalculado.toFixed(2) : "");
  }, [totalCalculado, precioBase]);

  return (
    <div className="row g-3">
      {showSearch && (
        <div className="col-md-12">
          <label className="form-label">Buscar estudiante por documento o nombre</label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Ingresa documento o escribe nombre/apellido"
              value={estudianteState.documento}
              onChange={(e) => {
                if (!isEdit) {
                  setDocumento?.(e.target.value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (!isEdit) buscarEstudiante?.();
                }
              }}
              disabled={isEdit || estudianteState.buscando}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => buscarEstudiante?.()}
              disabled={!puedeBuscar}
            >
              {estudianteState.buscando ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <i className="bi bi-search" aria-hidden="true" />
              )}
              <span className="ms-2">Buscar</span>
            </button>
          </div>
          {!isEdit && esNumerico && estudianteState.documento && estudianteState.documento.length < 10 && (
            <small className="text-muted">{10 - estudianteState.documento.length} digitos restantes (si es documento)</small>
          )}
          {!isEdit && estudianteState.documento.length >= 3 && estudianteState.buscando && (
            <small className="text-info">
              <i className="bi bi-search me-1" />
              Buscando estudiante...
            </small>
          )}
          {!isEdit && resultados.length > 0 && (
            <div className="mt-2">
              <small className="text-muted d-block mb-1">Selecciona el estudiante habilitado para matricula:</small>
              <div className="list-group small">
                {resultados.map((est) => (
                  <button
                    type="button"
                    key={est.id}
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-start"
                    onClick={() => seleccionarEstudiante?.(est, "nuevo")}
                  >
                    <div className="me-2">
                      <div className="fw-semibold">{`${est.nombres || ""} ${est.apellidos || ""}`.trim() || "Sin nombre"}</div>
                      <div className="text-muted">{est.estado_academico || ""}</div>
                    </div>
                    <small className="text-muted">{est.documento_identidad || "Sin documento"}</small>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="col-12">
        {!isEdit && <label className="form-label">Estudiante seleccionado</label>}
        <div className="border rounded bg-light p-3">
          {estudianteState.encontrado ? (
            <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
              <div className="fw-semibold">
                {`${estudianteState.encontrado.nombres || ""} ${estudianteState.encontrado.apellidos || ""}`.trim() ||
                  "Sin nombre"}
              </div>
            </div>
          ) : (
            <small className="text-muted">Busca un estudiante para seleccionarlo.</small>
          )}
        </div>
      </div>

      <div className="col-md-6">
        <label className="form-label">Estado matricula</label>
        <select
          className="form-select"
          value={form.estado_matricula}
          onChange={(e) => {
            const value = e.target.value;
            setForm((p) => ({
              ...p,
              estado_matricula: value,
              ciclo_id: value === "reservada" ? "" : p.ciclo_id,
              promocion_id: value === "reservada" ? "" : p.promocion_id,
              curso_id: value === "reservada" ? p.curso_id : "",
            }));
          }}
        >
          {ESTADOS_MATRICULA.map((estado) => (
            <option key={estado.value} value={estado.value}>
              {estado.label}
            </option>
          ))}
        </select>
        {estadoReservada && (
          <small className="text-muted d-block mt-1">
            Para reservas se elige curso directamente (sin ciclo ni promocion); se asigna una promocion al activar la matricula.
          </small>
        )}
      </div>

      {puedeVerFinalizadas && (
        <div className="col-12">
          <div className="p-2 border rounded bg-warning-subtle">
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="matricula-incluir-finalizadas"
                checked={Boolean(incluirFinalizadas)}
                disabled={loadingFinalizadas}
                onChange={(e) => {
                  const checked = e.target.checked;
                  onToggleIncluirFinalizadas?.(checked);
                  if (!checked && promoEstado === "finalizado") {
                    setForm((p) => ({ ...p, promocion_id: "", ciclo_id: "" }));
                  }
                }}
              />
              <label className="form-check-label" htmlFor="matricula-incluir-finalizadas">
                {loadingFinalizadas
                  ? "Cargando promociones finalizadas..."
                  : "Incluir promociones finalizadas (solo administracion, para corregir matriculas de migracion)"}
              </label>
            </div>
          </div>
        </div>
      )}

      {!estadoReservada && (
        <div className="col-12">
          <label className="form-label">Ciclo</label>
          <select
            className="form-select"
            value={cicloActivoId}
            onChange={(e) => {
              const value = e.target.value;
              setForm((p) => ({
                ...p,
                ciclo_id: value,
                promocion_id: "",
                curso_id: "",
              }));
            }}
            disabled={loadingOptions}
          >
            {renderOptions(ciclosConActual, "un ciclo", null, loadingOptions)}
          </select>
        </div>
      )}

      {!estadoReservada && (
        <div className="col-12">
          <label className="form-label">Promocion</label>
          <select
            className="form-select"
            value={form.promocion_id}
            onChange={(e) => {
              const value = e.target.value;
              const seleccion = promocionesConActual.find((opt) => String(opt.value) === String(value));
              setForm((p) => ({
                ...p,
                ciclo_id: value ? String(seleccion?.cicloId || p.ciclo_id || "") : p.ciclo_id,
                promocion_id: value,
                curso_id: value ? String(seleccion?.cursoId || "") : "",
              }));
            }}
            disabled={loadingOptions || !cicloActivoId}
          >
            {renderOptions(promocionesFiltradas, "una promocion", null, loadingOptions)}
          </select>
          {!cicloActivoId && (
            <small className="text-muted d-block mt-1">
              Selecciona primero el ciclo.
            </small>
          )}
          {promoYaInicio && (
            <small className="text-warning d-block mt-1">
              Esta promocion ya inicio; el estudiante quedara como cursando.
            </small>
          )}
          {promoEstado === "finalizado" && (
            <small className="text-danger d-block mt-1">
              Esta promocion ya finalizo. Solo disponible por permiso de administracion, para corregir matriculas de un corte de migracion.
            </small>
          )}
        </div>
      )}

      {mostrarCurso && (
        <div className="col-12">
          <label className="form-label">Curso (para reservas sin promocion)</label>
          <select
            className="form-select"
            value={form.curso_id}
            onChange={(e) => setForm((p) => ({ ...p, curso_id: e.target.value }))}
            disabled={loadingOptions}
          >
            {renderOptions(cursosOptions, "un curso", null, loadingOptions)}
          </select>
        </div>
      )}

      {variantes.length > 0 && (
        <div className="col-12">
          <label className="form-label">Variante / Subtipo{estadoReservada && " *"}</label>
          <select
            className="form-select"
            value={form.subtipo_curso_id || ""}
            onChange={(e) => setForm((p) => ({ ...p, subtipo_curso_id: e.target.value }))}
            disabled={loadingOptions}
            required={estadoReservada}
          >
            <option value="">Selecciona una variante</option>
            {variantes.map((v) => (
              <option key={v.id} value={v.id}>
                {v.nombre} (${Number(v.precio_base).toFixed(2)})
              </option>
            ))}
          </select>
          <small className="text-muted d-block mt-1">
            {estadoReservada
              ? "Obligatorio en reservas: indica que variante (ej. Reentrenamiento N1/N2) se esta reservando."
              : "Este curso tiene variantes comerciales con precio y certificado propios."}
          </small>
        </div>
      )}

      {isEdit && (
        <div className="col-md-6">
          <label className="form-label">Codigo matricula</label>
          <input
            className="form-control"
            value={codigoMatricula || codigoFallback}
            readOnly
          />
        </div>
      )}

      {reloadOptions && (
        <div className="col-12">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={reloadOptions}
            disabled={loadingOptions}
          >
            <i className="bi bi-arrow-repeat me-2" />
            {loadingOptions ? "Actualizando listas..." : "Actualizar listas"}
          </button>
        </div>
      )}
      {precioBase > 0 && (
        <div className="col-md-6">
          <label className="form-label">Total a pagar (con IVA 15%)</label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="form-control"
              value={precioInput}
              placeholder={(precioBase * (1 - descPromoPct / 100) * 1.15).toFixed(2)}
              onChange={(e) => {
                const val = e.target.value;
                setPrecioInput(val);
                editandoPrecioRef.current = true;
                const num = parseFloat(val);
                if (!isNaN(num) && num >= 0 && precioBase > 0) {
                  const subtotalDeseado = num / 1.15;
                  const descPromoMonto = precioBase * (descPromoPct / 100);
                  const descIndMonto = precioBase - descPromoMonto - subtotalDeseado;
                  const pct = Math.round(Math.max(0, (descIndMonto / precioBase) * 100) * 10000) / 10000;
                  setForm((p) => ({ ...p, descuento_individual: pct }));
                }
              }}
              onBlur={() => {
                editandoPrecioRef.current = false;
                setPrecioInput(precioBase && totalCalculado > 0 ? totalCalculado.toFixed(2) : "");
              }}
            />
          </div>
          <small className="text-muted mt-1 d-block">
            Precio lista: ${(precioBase * (1 - descPromoPct / 100) * 1.15).toFixed(2)}
          </small>
        </div>
      )}
      <div className="col-md-6">
        <label className="form-label">Descuento individual (%)</label>
        <input
          type="number"
          min="0"
          max="100"
          step="0.0001"
          className="form-control"
          value={form.descuento_individual}
          onChange={(e) => {
            editandoPrecioRef.current = false;
            setForm((p) => ({ ...p, descuento_individual: Number(e.target.value) || 0 }));
          }}
        />
        {descPromoPct > 0 && (
          <small className="text-muted mt-1 d-block">
            + {descPromoPct}% descuento de promoción ya incluido
          </small>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">Modalidad</label>
        <select
          className="form-select"
          value={form.modalidad || 'presencial'}
          onChange={(e) => setForm((p) => ({ ...p, modalidad: e.target.value }))}
        >
          <option value="presencial">Presencial</option>
          <option value="online">Online / A distancia</option>
        </select>
      </div>

      <div className="col-md-6 d-flex align-items-center">
        <div className="w-100">
          <label className="form-label">Activo</label>
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id={`matricula-activa-${isEdit ? "edit" : "new"}`}
              checked={Boolean(form.activo)}
              onChange={(e) => setForm((p) => ({ ...p, activo: e.target.checked }))}
            />
            <label className="form-check-label" htmlFor={`matricula-activa-${isEdit ? "edit" : "new"}`}>
              {form.activo ? "Activa" : "Inactiva"}
            </label>
          </div>
        </div>
      </div>

      {puedeEditarFecha && !isEdit && (
        <div className="col-12">
          <div className="p-2 border rounded bg-warning-subtle">
            <label className="form-label mb-1">Fecha de matrícula (migración)</label>
            <input
              type="datetime-local"
              className="form-control form-control-sm"
              value={form.fecha_matricula || ""}
              onChange={(e) => setForm((p) => ({ ...p, fecha_matricula: e.target.value }))}
            />
          </div>
        </div>
      )}

      <div className="col-12">
        <label className="form-label">Observaciones</label>
        <textarea
          className="form-control"
          rows="2"
          value={form.observaciones}
          onChange={(e) => setForm((p) => ({ ...p, observaciones: e.target.value }))}
          placeholder="Notas internas"
        />
      </div>
    </div>
  );
}
